/* ═══════════════════════════════════════════════════════════════════════════════
   TourInfoCard: Centered Info Card for Mid-Tour Education

   Renders a centered modal card with step-specific title, icon, and description.
   Unlike TourWelcome (which shows the tour-level intro), this component shows
   content from the individual step, used for teaching concepts that have no
   DOM target (e.g., showing a sample of what a split cell looks like).

   Includes progress bar and Next/Back/Skip navigation.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { motion } from 'framer-motion';
import type { TourStep } from '../../lib/tours/types';

interface TourInfoCardProps {
  step: TourStep;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

export function TourInfoCard({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: TourInfoCardProps) {
  const progressPct = ((currentStep + 1) / totalSteps) * 100;

  return (
    <motion.div
      className="tour-welcome-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="tour-welcome-modal"
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 32, delay: 0.06 }}
      >
        <div className="tour-welcome-body">
          {/* Progress bar */}
          <div className="tour-progress">
            <div className="tour-progress-bar">
              <div
                className="tour-progress-fill"
                style={{ width: `${progressPct}%`, transition: 'width 0.4s ease' }}
              />
            </div>
            <span className="tour-progress-text">
              {currentStep + 1} / {totalSteps}
            </span>
          </div>

          {/* Icon */}
          {step.icon && (
            <motion.div
              style={{ fontSize: '2rem', marginBottom: '0.75rem' }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.25 }}
            >
              {step.icon}
            </motion.div>
          )}

          {/* Title */}
          <motion.h2
            className="tour-welcome-title"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {step.title}
          </motion.h2>

          {/* Description: renders newlines as line breaks */}
          <motion.div
            className="tour-tooltip-description"
            style={{ whiteSpace: 'pre-line', lineHeight: 1.7, marginBottom: '1.25rem' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.3 }}
          >
            {step.description}
          </motion.div>

          {/* Navigation */}
          <motion.div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.3 }}
          >
            <button className="tour-btn-skip" onClick={onSkip}>
              Skip tour
            </button>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {currentStep > 0 && (
                <button className="tour-btn-secondary" onClick={onPrev}>
                  ← Back
                </button>
              )}
              <button className="tour-btn-primary" onClick={onNext}>
                {step.actionLabel || 'Next →'}
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
